import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;
export const API = `${BASE}/api`;

// Cookies (httpOnly `access_token`) are the primary auth mechanism.
// `withCredentials: true` ensures the browser attaches them on every request.
// The Authorization header path in the backend `get_current_user` is kept as
// a fallback for legacy sessions and share-link `?auth=<JWT>` links.
export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Auto-retry transient gateway errors (backend hot-reload window, brief upstream hiccups).
// Applies to 502 / 503 / 504 and network errors — up to 2 retries with 1s + 2s backoff.
api.interceptors.response.use(
  (r) => r,
  async (err) => {
    const cfg = err?.config;
    if (!cfg || cfg.__isRetry >= 2) throw err;
    const status = err?.response?.status;
    const retriable = !err?.response || status === 502 || status === 503 || status === 504;
    // Never retry non-idempotent user-triggered actions where a second submit is dangerous:
    // POST /auth/register (creates users), POST /billing/* (Stripe checkout sessions)
    const url = (cfg.url || "").toLowerCase();
    const nonRetryable = /\/auth\/register|\/billing\//.test(url);
    if (!retriable || nonRetryable) throw err;
    cfg.__isRetry = (cfg.__isRetry || 0) + 1;
    const delay = cfg.__isRetry === 1 ? 1000 : 2000;
    await new Promise((res) => setTimeout(res, delay));
    return api(cfg);
  }
);

export function formatApiError(err) {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  // iter-24 quota banner: structured payloads from AI + billing endpoints
  // carry a machine-readable ``code``. Return the human-readable ``message``
  // so ``toast.error(formatApiError(e))`` never renders an object as a
  // React child. Callers that need the structured shape read the raw
  // ``err.response.data.detail`` directly (see ``isQuotaError``).
  if (detail && typeof detail === "object" && !Array.isArray(detail) && detail.code) {
    return String(detail.message || detail.code || "Something went wrong");
  }
  if (status === 502 || status === 503 || status === 504) {
    return "The server is briefly unavailable (upstream 502/503). We already retried — please try again in a moment.";
  }
  if (!detail) return err?.message || "Something went wrong";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e?.msg ? e.msg : JSON.stringify(e))).join(" ");
  return JSON.stringify(detail);
}

/** Detect the iter-24 quota-exceeded shape returned by
 *  ``formatApiError`` when the backend flags a Nano Banana rate/balance
 *  error. Callers use this to render an inline banner. */
export function isQuotaError(errOrFormatted) {
  const d = errOrFormatted?.response?.data?.detail
            ?? (typeof errOrFormatted === "object" ? errOrFormatted : null);
  return !!(d && d.code === "llm_quota_exceeded");
}
